import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import type { RestQueryHookData } from '@main/core-ui';
import {
  logger,
  SKIP_PRIVACY_CENTER_MODULES_FLAG_KEY,
  useQueryParam,
  useRestQuery,
} from '@main/core-ui';
import { ONE_DAY } from '@main/utils';

import { PRIVACY_CENTER_STATIC_PREFIX } from '../args';

/**
 * the amount of time before the flag to skip modules times out, and will revert
 *  to viewing the live version
 */
const SKIP_MODULES_FLAG_TIMEOUT = 2 * ONE_DAY;

/**
 * check if the skip modules flag value is a date and if it's expired
 *
 * @param skipFlagValue - the value to check
 * @returns if the flag is a date and expired
 */
function isDateFlag(skipFlagValue: string | null): {
  /** is the flag a date */
  isFlagValueDate: boolean;
  /** is the date flag expired */
  isFlagExpired: boolean;
} {
  const isFlagValueDate = !Number.isNaN(Date.parse(skipFlagValue || ''));
  const isFlagExpired =
    isFlagValueDate &&
    Date.parse(skipFlagValue || '') < Date.now() - SKIP_MODULES_FLAG_TIMEOUT;

  return { isFlagValueDate, isFlagExpired };
}

/**
 * create a static privacy center module url
 *
 * @param options - Options
 * @returns the url
 */
export function createStaticPrivacyCenterModuleUrl({
  module,
  locale,
  host = window.location.host,
}: {
  /** the module to fetch */
  module: string;
  /** the locale to fetch */
  locale?: string;
  /** the host to fetch from */
  host?: string;
}): string {
  return `${
    // static prefix for local development support
    PRIVACY_CENTER_STATIC_PREFIX || ''
  }/static/${host}/${module}${locale ? `-${locale}` : ''}.json`;
}

/**
 * Hook to fetch static privacy center module data
 *
 * @deprecated Use `buildUseCdnQueryWithFallback` instead
 * @param module - module to fetch
 * @param locale - locale to fetch
 * @returns data from the module
 */
export const useStaticPrivacyCenterModule = <T>(
  module: string,
  locale?: string,
): {
  /** whether to skip fetching from gql */
  skipGql: boolean;
  /** the data from the rest request */
  directData: RestQueryHookData<T>['data'];
  /** the error from the rest request */
  directError: RestQueryHookData<T>['gqlError'];
  /** the loading state from the rest request */
  directLoading: RestQueryHookData<T>['loading'];
} => {
  // check if skip modules flag is set (cypress)
  const { value: skipQueryParam, persistQueryParam } = useQueryParam<string>({
    name: SKIP_PRIVACY_CENTER_MODULES_FLAG_KEY,
  });
  const skipFlagValue = localStorage.getItem(
    SKIP_PRIVACY_CENTER_MODULES_FLAG_KEY,
  );
  const { isFlagValueDate, isFlagExpired } = isDateFlag(skipFlagValue);

  const skipDirect =
    // if skipQueryParam is false, we should ignore all other indicators
    skipQueryParam !== 'false' &&
    (skipQueryParam === 'true' ||
      skipFlagValue === 'true' ||
      (isFlagValueDate && !isFlagExpired) ||
      !!(window as any).Cypress);
  if (skipDirect) {
    if (skipFlagValue !== 'true' || !isFlagValueDate) {
      localStorage.setItem(
        SKIP_PRIVACY_CENTER_MODULES_FLAG_KEY,
        new Date().toISOString(),
      );
    }
  }
  // clear out if false
  if (!skipDirect && !!skipFlagValue) {
    localStorage.removeItem(SKIP_PRIVACY_CENTER_MODULES_FLAG_KEY);
  }
  if (skipQueryParam) {
    persistQueryParam(undefined);
  }

  const url = createStaticPrivacyCenterModuleUrl({ module, locale });
  const {
    data: directData,
    gqlError: directError,
    loading: directLoading,
    error: rawError,
  } = useRestQuery<T>({
    url,
    skip: skipDirect,
  });

  useEffect(() => {
    if (directError) {
      logger.warn(
        `Privacy Center assets used fallback endpoint for asset: ${url}`,
        directError,
        rawError,
      );
    }
  }, [directError]);

  // fall back to gql if direct query fails
  const skipGql = !directError && !skipDirect;

  return { directData, directError, directLoading, skipGql };
};

export const useIsDraftPrivacyCenter = (): {
  /** is the privacy center a draft version? */
  isDraft: boolean;
  /** clears the draft flag and loads the live version */
  viewLive: () => void;
} => {
  const location = useLocation();

  return useMemo(() => {
    const skipFlagValue = localStorage.getItem(
      SKIP_PRIVACY_CENTER_MODULES_FLAG_KEY,
    );
    const { isFlagValueDate, isFlagExpired } = isDateFlag(skipFlagValue);

    return {
      isDraft: skipFlagValue === 'true' || (isFlagValueDate && !isFlagExpired),
      viewLive: () => {
        localStorage.removeItem(SKIP_PRIVACY_CENTER_MODULES_FLAG_KEY);
        window.location.reload();
      },
    };
  }, [location]);
};
